import { readAuthToken } from '@noor/api';
import { USER_TYPE_MERCHANT, legacyPermissions } from '../config/permissions';

/**
 * The JWT claims the old panel's access check reads — transcribed from its
 * `utils/access.ts` (`AuthClaims`) and `Routes.tsx` (`decodeJWT`).
 */
export interface LegacyAuthClaims {
  role?: string;
  user_type?: string;
  id?: string | number;
  exp?: number;
}

/**
 * Decode a JWT payload WITHOUT verifying — exactly what the old panel's
 * `jwtDecode` call did (signature verification is the backend's job; the UI
 * only hides affordances). Anything malformed yields `null`, mirroring the
 * source's try/catch-returns-null — and a null claim set denies everything
 * except through the merchant bypass, which also needs valid claims.
 */
export function decodeLegacyAuthClaims(token: string | null): LegacyAuthClaims | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const claims = JSON.parse(json) as LegacyAuthClaims;
    if (!claims || typeof claims !== 'object') return null;
    return claims;
  } catch {
    return null;
  }
}

/**
 * The signed-in session's claims, read from the stored access token — the old
 * `getAuthClaims()`. The storage KEY differs (`auth_token` here vs the old
 * app's `access_token`) because each app owns its storage; the decode
 * semantics are identical.
 */
export function getLegacyAuthClaims(): LegacyAuthClaims | null {
  if (typeof localStorage === 'undefined') return null;
  return decodeLegacyAuthClaims(readAuthToken());
}

// The main merchant (business) account has full access on top of the
// role-based checks for employees (merchant-admin, manager, cashier).
// Verbatim from the old `isMerchantAccount()`.
export function isLegacyMerchantAccount(claims: LegacyAuthClaims | null): boolean {
  return claims?.user_type === USER_TYPE_MERCHANT || claims?.role === USER_TYPE_MERCHANT;
}

/**
 * Pure permission check — the old `CheckAccess` predicate:
 * `<resource>.<action>` (action defaults to `"view"`), granted when the
 * merchant bypass holds or the JWT role is listed for that matrix cell.
 * Unknown resources/actions deny, as the source's chained `&&` does.
 */
export function hasLegacyPermission(
  claims: LegacyAuthClaims | null,
  permission: string,
): boolean {
  if (isLegacyMerchantAccount(claims)) return true;
  const [resource, action = 'view'] = permission.split('.');
  if (!resource) return false;
  return Boolean(legacyPermissions[resource]?.[action]?.includes(claims?.role ?? ''));
}
